/**
 * Pure tool-call presentation helpers shared by every surface (A-2) —
 * no React, no DOM, no I/O. Moved verbatim from
 * `frontend/src/components/agent/toolCallHelpers.ts`; the web file is now a
 * re-export facade of this module.
 */

import { stripMcpPrefix } from "./toolNames.ts";

const TOOL_LABELS: Record<string, string> = {
  "email.get": "Read email",
  "email.list": "List emails",
  "email.reply": "Reply to email",
  "email.batch": "Read emails",
  "contacts.get": "Read contact",
  "contacts.list": "List contacts",
  "contacts.search": "Search contacts",
  "companies.get": "Read company",
  "companies.list": "List companies",
  "tasks.get": "Read task",
  "tasks.list": "List tasks",
  "tasks.search": "Search tasks",
  "meetings.get": "Read meeting",
  "meetings.list": "List meetings",
  "meetings.search": "Search meetings",
  "telegram.messages.list": "Read messages",
  "telegram.messages.send": "Send message",
  "telegram.chats.list": "List chats",
  "telegram.search": "Search Telegram",
  "graph.entity.get": "Look up entity",
  "graph.entity.links": "Look up connections",
  "search.hybrid": "Search",
  "search.semantic": "Search",
  "search.fast": "Search",
  "triggers.list": "List triggers",
  "triggers.get": "Get trigger",
};

/** Harness/plumbing tools that are not user-meaningful actions and must be
 *  hidden from the transcript: episode bookkeeping and the Claude Code schema
 *  loader (`ToolSearch`, which also leaks raw `mcp__…__` ids in its args). */
export function isHiddenTool(name: string): boolean {
  const clean = stripMcpPrefix(name);
  return (
    clean.includes("set_title") ||
    clean.includes("append_message") ||
    clean === "ToolSearch" ||
    clean.startsWith("ToolSearch") ||
    // @tested-by: tst_fe_presentation_task_001
    // @invariant: INV-13 — the CLI engines' own bookkeeping is not a product
    // action. TaskCreate/TaskUpdate render as their own cards in the Todo
    // region; leaving them in the transcript too showed the user the agent's
    // scratchpad and pushed the real approval cards off screen.
    clean === "TaskCreate" ||
    clean === "TaskUpdate"
  );
}

/** Human-readable tool label: uses known mapping, falls back to spaced name.
 *  Always strips the internal MCP server namespace first. */
export function humanizeToolName(name: string): string {
  const bare = stripMcpPrefix(name);
  // Normalize to dot notation for LABEL LOOKUP ONLY — this replaces ALL
  // underscores, which is wrong for RPC dispatch (use rpcNameForToolCall).
  const dotName = bare.replace(/__/g, ".").replace(/_/g, ".");
  if (TOOL_LABELS[dotName]) return TOOL_LABELS[dotName];
  return bare.replace(/[._]/g, " ");
}

/**
 * Pull a user-readable label out of tool args — what was being asked /
 * created / searched. Used to render the row as
 * "**<verb>**: <query>" instead of just the verb. Keys are checked in
 * preference order; first match wins.
 */
export function extractArgSummary(args: unknown): string | undefined {
  if (args === null || typeof args !== "object") return undefined;
  const o = args as Record<string, unknown>;
  for (const key of ["query", "name", "title", "subject"] as const) {
    const v = o[key];
    if (typeof v === "string" && v.length > 0) return v;
  }
  return undefined;
}

/** Extract a result count from a tool result object. */
export function extractResultCount(result: unknown): number | null {
  if (result == null || typeof result !== "object") return null;
  const obj = result as Record<string, unknown>;
  if (typeof obj.total === "number") return obj.total;
  if (Array.isArray(obj.items)) return obj.items.length;
  if (typeof obj.count === "number") return obj.count;
  return null;
}

/**
 * Display label for the result-count badge. For a BOUNDED window
 * ({items, total} where total exceeds the returned items) shows
 * "<returned> of <total>" — e.g. "50 of 27859" — so a capped retrieval reads as
 * what reached the agent vs. what exists, instead of the bare total, which
 * looks like the whole set was dumped into context.
 */
export function extractResultCountLabel(result: unknown): string | null {
  if (result == null || typeof result !== "object") return null;
  const obj = result as Record<string, unknown>;
  const items = Array.isArray(obj.items) ? obj.items.length : null;
  const total = typeof obj.total === "number" ? obj.total : null;
  if (items != null && items > 0 && total != null && total > items) {
    return `${String(items)} of ${String(total)}`;
  }
  if (total != null) return String(total);
  if (items != null) return String(items);
  if (typeof obj.count === "number") return String(obj.count);
  return null;
}

/** Wire marker set on a tool_result when the tool THREW during execution
 *  (builtin engine, AI_ToolExecutionError). Lets every surface render a
 *  terminal error card instead of an infinite spinner. */
export const TOOL_ERROR_MARKER = "__tool_error";

/** If a tool_result is a tool-execution error, return its message; else null.
 *  The marker (not a bare `error` field) is required so a normal result that
 *  merely carries an `error` key is not mistaken for a failed tool. */
export function toolResultError(result: unknown): string | null {
  if (result == null || typeof result !== "object") return null;
  const r = result as Record<string, unknown>;
  if (r[TOOL_ERROR_MARKER] !== true) return null;
  return typeof r.message === "string" && r.message.trim().length > 0
    ? r.message
    : "Tool execution failed";
}

/** Build a copyable JSON string from tool args + result. */
export function buildCopyPayload(args: unknown, result: unknown): string {
  return JSON.stringify(
    { input: args ?? null, output: result ?? null },
    null,
    2,
  );
}
